/**
 * Deterministic PRNG. `mulberry32` — 32-bit state, fast, good enough distribution
 * for a game simulation, and trivially serialisable into a save file.
 *
 * NOTHING in `src/sim` may call `Math.random()`. Every stochastic decision goes
 * through one of these functions, seeded from the run seed. A test enforces this.
 */

export interface Rng {
  /** Current 32-bit state. Serialise this and the stream resumes exactly. */
  s: number;
}

export function makeRng(seed: number): Rng {
  return { s: seed >>> 0 };
}

export function cloneRng(r: Rng): Rng {
  return { s: r.s };
}

/** Raw next 32-bit unsigned integer. */
export function nextU32(r: Rng): number {
  r.s = (r.s + 0x6d2b79f5) >>> 0;
  let t = r.s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (t ^ (t >>> 14)) >>> 0;
}

/** Uniform in [0, 1). */
export function nextFloat(r: Rng): number {
  return nextU32(r) / 4294967296;
}

/** Uniform in [min, max). */
export function nextRange(r: Rng, min: number, max: number): number {
  return min + (max - min) * nextFloat(r);
}

/** Uniform integer in [min, max] inclusive. */
export function nextInt(r: Rng, min: number, max: number): number {
  if (max <= min) return min;
  return min + (nextU32(r) % (max - min + 1));
}

/** True with probability p. */
export function chance(r: Rng, p: number): boolean {
  return nextFloat(r) < p;
}

/** Uniform pick. Returns undefined only for an empty array. */
export function pick<T>(r: Rng, arr: readonly T[]): T {
  return arr[nextInt(r, 0, arr.length - 1)];
}

/** Weighted pick. `weights` must be the same length as `arr` and sum > 0. */
export function pickWeighted<T>(r: Rng, arr: readonly T[], weights: readonly number[]): T {
  let total = 0;
  for (let i = 0; i < weights.length; i++) total += Math.max(0, weights[i]);
  if (total <= 0) return arr[0];
  let roll = nextFloat(r) * total;
  for (let i = 0; i < arr.length; i++) {
    roll -= Math.max(0, weights[i]);
    if (roll <= 0) return arr[i];
  }
  return arr[arr.length - 1];
}

/** In-place Fisher-Yates. */
export function shuffle<T>(r: Rng, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = nextInt(r, 0, i);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/** Approximately normal (sum of 3 uniforms — cheap, bounded, no trig). */
export function gauss(r: Rng, mean: number, sd: number): number {
  const u = nextFloat(r) + nextFloat(r) + nextFloat(r) - 1.5;
  return mean + u * sd * 1.4142;
}

/** A signed unit-ish jitter in [-1, 1). */
export function jitter(r: Rng): number {
  return nextFloat(r) * 2 - 1;
}

/**
 * Derive an independent stream from a parent seed plus a salt. Used so that,
 * e.g., map generation and the invasion director do not consume from the same
 * stream (which would make one system's changes perturb the other's output).
 */
export function deriveSeed(seed: number, salt: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (salt >>> 0), 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Turn a human-typed seed string into a 32-bit seed. */
export function seedFromString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Render a seed back to a short, typeable code. */
export function seedToCode(seed: number): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let v = seed >>> 0;
  let out = '';
  for (let i = 0; i < 7; i++) {
    out += alphabet[v % 32];
    v = Math.floor(v / 32);
  }
  return out;
}
